/**
 * Type definitions for the Position Opener / Entry feature.
 *
 * Mirrors the unwinding types module but inverted for BUY-side semantics:
 * - Unwinding "contractsToSell" → entry "contractsToBuy"
 * - Unwinding "contractsSoldKalshi/Polymarket" → entry "contractsBoughtKalshi/Polymarket"
 *
 * The entry engine evaluates whether the SUM of both legs' ASK prices is
 * below a user-configured threshold AND below the absolute hard cap of
 * $0.999 (anything >= $1 is unprofitable by construction — buying both
 * sides of a binary market for >= $1 cannot yield arbitrage).
 */

export interface TopOfBook {
  price: number;
  size: number;
}

/**
 * Hard upper bound for the sum of ASKs at which an entry order may be
 * placed. Enforced in three places on the frontend (input validator,
 * confirmation banner, Activate button disabled state) and again on the
 * backend. There is intentionally no admin override.
 */
export const ENTRY_HARD_CAP_GROSS = 0.999;

/**
 * Default target TNA percent suggested for new entry rules. Operators
 * adjust per pair — this is just the seeded value when creating a rule.
 */
export const DEFAULT_TARGET_TNA_PERCENT = 40;

export interface EntrySimulationBreakdown {
  rawTakerAsk: number;
  netTakerAsk: number;
  takerFeeRate: number;
  takerFeeAmount: number;
  takerPlatform: string;
  targetTna: number;
  daysToExpiry: number;
  effectiveDays: number;
  maxBuyPrice: number;
  makerBidTopPrice: number;
  makerBidTopSize: number;
  joinedExistingLevel: boolean;
  requestedSize: number;
  takerLiquidityCap: number;
  pairCap: number;
  effectiveSize: number;
  makerPrice: number;
  makerFeeRate: number;
  makerFeeAmount: number;
  netMakerPrice: number;
  achievedTna: number;
  unplaceableNoMillis?: boolean;
}

export interface EntrySimulationResult {
  makerPrice: number;
  takerPrice: number;
  tna: number;
  effectiveSize: number;
  breakdown?: EntrySimulationBreakdown;
}

export type EntryRuleStatus = 'active' | 'paused' | 'completed' | 'cancelled';
export type EntryRuleType = 'tna' | 'sum_of_sides';
export type EntryStrategy = 'maker-first' | 'taker-taker';

export interface EnrichedEntryStatus {
  ruleId: string;
  pairId?: string;
  state: string;
  makerPlatform: 'kalshi' | 'polymarket';
  makerOrderId: string | null;
  makerPrice: number | null;
  makerFilledQty: number;
  makerFilledPrice: number;
  counterOrderId: string | null;
  counterFilledQty: number;
  lastStateChange: string;
  errorMessage: string | null;
  pairName: string;
  expiryDate: string;
  daysToExpiry: number;
  polyBid: TopOfBook | null;
  polyAsk: TopOfBook | null;
  kalshiBid: TopOfBook | null;
  kalshiAsk: TopOfBook | null;
  /** Sum of both legs' ASK prices — the entry-side analogue of the
   *  unwinder's TT. When this drops below the rule's threshold AND below
   *  ENTRY_HARD_CAP_GROSS, the engine fires. */
  currentSumOfAsks: number | null;
  simulation: {
    polyAsMaker: EntrySimulationResult | null;
    kalshiAsMaker: EntrySimulationResult | null;
  };
  kalshiConfig: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent: number;
  } | null;
  polymarketConfig: {
    side: 'yes' | 'no';
    // Both outcome tokens; `tokenId` is the backend virtual derived from
    // `side` (side === 'yes' ? yesTokenId : noTokenId).
    yesTokenId: string;
    noTokenId: string;
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent: number;
  } | null;
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  volumeUsed: number;
  volumeRemaining: number;
  /**
   * Per-`(path, leg)` "this leg hit its maxV cap and was disarmed" markers.
   * Drives the exclamation icon next to each tab's MaxV box; cleared on
   * re-arm / MaxV change. Defaults to all-false when the backend omits it.
   */
  maxVolumeHit?: {
    a: { kalshi: boolean; polymarket: boolean };
    b: { kalshi: boolean; polymarket: boolean };
  } | null;
  kalshiMarketUrl: string | null;
  polymarketMarketUrl: string | null;
  simTna?: number;
  simSize?: number;
  simMaxVolume?: number;
  appliedSimTna?: number;
  appliedSimSize?: number;
  appliedSimMaxVolume?: number;
  hasPendingSim?: boolean;

  // ── Two-path market data (BUY side) ─────────────────────────────────────
  // A prediction-market arb has two entry paths; the enricher emits
  // both sides' quotes for both platforms so the row can show both
  // sums while market data + sim cells track the lower-sum / committed
  // path. See position-opener's `EnrichedRuleState` for the contract.
  polyYesBid?: TopOfBook | null;
  polyYesAsk?: TopOfBook | null;
  polyNoBid?: TopOfBook | null;
  polyNoAsk?: TopOfBook | null;
  kalshiYesBid?: TopOfBook | null;
  kalshiYesAsk?: TopOfBook | null;
  kalshiNoBid?: TopOfBook | null;
  kalshiNoAsk?: TopOfBook | null;
  /** Gross ask sum for Path A (poly_yes_ask + kalshi_no_ask). */
  sumPathA?: number | null;
  /** Gross ask sum for Path B (poly_no_ask + kalshi_yes_ask). */
  sumPathB?: number | null;
  /** Which path currently has the lower gross sum. */
  lowerPath?: 'a' | 'b' | null;
  /**
   * Non-null when contracts have been bought on this rule. Locks the
   * UI to the committed path: only its 2 toggles are editable and
   * only its sum/market-data render.
   */
  committedPath?: 'a' | 'b' | null;
  /** Per-leg maker sim cells (orange = taker net, blue = maker rest). */
  simPolyYesMaker?: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker?: { taker: number | null; maker: number | null } | null;
  /**
   * Engine-parity sim: depth-walked VWAP taker prices + maker rest per
   * path. The maker rest is the maker leg's OWN live top-of-book bid
   * (where a BUY maker rests), so `sumPolyMk = ownBid + counterTakerAllIn`
   * is the live book sum — compared against `thresholdUsed` it surfaces
   * the gap. Computed by the same helpers the engine uses to trigger
   * orders. When present, the FE prefers these over its own client-side
   * approximations. Falls back to client math when null (legacy envelope
   * or pre-rule pair with no orderbook).
   */
  engineSim?: EngineSim | null;
  /**
   * The LIVE engine's believed position for this rule — what it thinks we
   * hold per (path, platform) right now, ahead of Mongo + the venue UIs.
   * `null` until a live envelope arrives. Powers the "Engine position" drawer.
   */
  believedPosition?: BelievedPosition | null;
  /**
   * Per-pair opener kill switch (mirrors backend
   * `OpeningPageState.pairEnabled`). Drives the per-row switch + gates
   * the activate button. Newly manual-confirmed pairs default to false.
   */
  pairEnabled?: boolean;
}

export interface EnginePathPrices {
  polyMakerRest: number | null;
  kalshiMakerRest: number | null;
  /**
   * The maker leg's ACTUAL resting placement price (threshold-capped),
   * vs `*MakerRest` which is the live own-bid. Can be lower than the bid
   * when the threshold binds. `null` when the engine emits no placement.
   */
  polyMakerPlace: number | null;
  kalshiMakerPlace: number | null;
  polyTakerAllInVwap: number | null;
  kalshiTakerAllInVwap: number | null;
  sumTT: number | null;
  sumPolyMk: number | null;
  sumKalshiMk: number | null;
}

export interface EngineSim {
  a: EnginePathPrices;
  b: EnginePathPrices;
  sizeUsed: number;
  thresholdUsed: number;
}

// ── Phase 6 — split-shape envelope ──────────────────────────────────────
// The new `OpeningPageState` decouples pair-scoped market data from
// rule-scoped engine state. The hook listens to `opening_state` /
// `opening_state_update` and projects these envelopes back into the
// legacy `EnrichedEntryStatus` shape so existing row components keep
// working. Phase 7 will rewrite the row components to consume the
// split shape directly and the projection will be removed.
export interface MarketState {
  pairId: string;
  pairName: string;
  expiryDate: string;
  daysToExpiry: number;
  polyYesBid: TopOfBook | null;
  polyYesAsk: TopOfBook | null;
  polyNoBid: TopOfBook | null;
  polyNoAsk: TopOfBook | null;
  kalshiYesBid: TopOfBook | null;
  kalshiYesAsk: TopOfBook | null;
  kalshiNoBid: TopOfBook | null;
  kalshiNoAsk: TopOfBook | null;
  kalshiMarketUrl: string | null;
  polymarketMarketUrl: string | null;
}

export interface OpenerRuleState {
  ruleId: string;
  state: string;
  makerPlatform: 'kalshi' | 'polymarket';
  makerOrderId: string | null;
  makerPrice: number | null;
  makerFilledQty: number;
  makerFilledPrice: number;
  counterOrderId: string | null;
  counterFilledQty: number;
  lastStateChange: string;
  errorMessage: string | null;
  kalshiConfig: EnrichedEntryStatus['kalshiConfig'];
  polymarketConfig: EnrichedEntryStatus['polymarketConfig'];
  simulation: EnrichedEntryStatus['simulation'];
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  volumeUsed: number;
  volumeRemaining: number;
  simTna?: number;
  simSize?: number;
  simMaxVolume?: number;
  appliedSimTna?: number;
  appliedSimSize?: number;
  appliedSimMaxVolume?: number;
  hasPendingSim?: boolean;
  kalshiOrderState: unknown;
  polyOrderState: unknown;
  sumPathA: number | null;
  sumPathB: number | null;
  lowerPath: 'a' | 'b' | null;
  committedPath: 'a' | 'b' | null;
  simPolyYesMaker: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker: { taker: number | null; maker: number | null } | null;
  maxVolumeHit?: {
    a: { kalshi: boolean; polymarket: boolean };
    b: { kalshi: boolean; polymarket: boolean };
  } | null;
}

// ── Engine believed position ─────────────────────────────────────────────
// The LIVE engine's in-memory truth of what we hold per (path, platform),
// emitted on every fill / placement — ahead of the persisted Mongo state and
// the venue dashboards (both lag). Mirror of the backend wire `BelievedPosition`.
export interface PlatformBelief {
  /** Net believed contracts — opener: `starting + totalBought`; unwinder: `available`. */
  net: number;
  starting: number;
  totalBought: number;
  /** Available-to-sell (unwinder; never clamped — negative = oversold). */
  available: number;
  contractsBoughtCycle: number;
  /** Contracts bought on this leg-thread THIS ON-session (opener; resets on
   *  the leg's off→on edge). The "in memory for this session" figure. */
  sessionContractsBought: number;
  volumeExecuted: number;
  maxVolume: number;
  makerFilledQty: number;
  counterFilledQty: number;
  unhedgedPartial: number;
  supersededTotal: number;
  isMaker: boolean;
  side: 'yes' | 'no';
  makerPrice?: number | null;
  makerOrderId?: string | null;
}

export interface PathBelief {
  path: 'a' | 'b';
  kalshi: PlatformBelief;
  polymarket: PlatformBelief;
}

export interface BelievedPosition {
  direction: 'buy' | 'sell';
  bidirectional: boolean;
  /** Signed net believed Kalshi contracts across both paths (`+` net-long NO). */
  kalshiNet: number;
  /** Signed net believed Polymarket contracts across both paths (`+` net-long YES). */
  polyNet: number;
  /** `|kalshiNet − polyNet|` — cross-path hedge imbalance (0 = delta-neutral). */
  legImbalance: number;
  paths: PathBelief[];
}

export interface OpeningPageState {
  pairId: string;
  market: MarketState;
  rule: OpenerRuleState | null;
  /**
   * Per-pair opener kill switch — mirrors the backend's
   * `OpeningPageState.pairEnabled`. Newly manual-confirmed pairs default
   * to false; the row renders OFF (no market data, activate button
   * disabled) until the operator turns the switch on.
   */
  pairEnabled?: boolean;
  // Always-computed (backend) path + sim payload — populated for
  // every pair regardless of whether a rule is active. The threshold
  // used for sim cells is `rule.thresholdSum` when the rule exists,
  // else `OPENER_PREVIEW_THRESHOLD` (0.95) on the backend.
  sumPathA?: number | null;
  sumPathB?: number | null;
  lowerPath?: 'a' | 'b' | null;
  simPolyYesMaker?: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker?: { taker: number | null; maker: number | null } | null;
  /** Engine-parity sim — see EnrichedEntryStatus.engineSim. */
  engineSim?: EngineSim | null;
  /**
   * The LIVE engine's believed position for this rule. `null` until a live
   * envelope arrives. Powers the per-row "Engine position" drawer.
   */
  believedPosition?: BelievedPosition | null;
}

export type MakerFlag =
  | 'polyYesMaker'
  | 'polyNoMaker'
  | 'kalshiYesMaker'
  | 'kalshiNoMaker';

/** Minimal per-path counter shape consumed by the FE — only the
 *  threshold is read/written today. */
export interface PathCounters {
  thresholdSum?: number | null;
}

export interface EntryRule {
  _id: string;
  pairId: string;
  pairName: string;
  expiryDate: string;
  customExpiryDate?: string | null;
  category?: string;
  ruleType?: EntryRuleType;
  /** Pair-level threshold for sum_of_sides rules; the engine fires when
   *  currentSumOfAsks <= thresholdSum AND < ENTRY_HARD_CAP_GROSS. Used as
   *  the create-time seed for both paths; per-path edits live on
   *  `pathA`/`pathB`. */
  thresholdSum?: number | null;
  /** Per-path counters. Each tab (Path A = Poly YES + Kalshi NO, Path B =
   *  Poly NO + Kalshi YES) edits its OWN threshold via these. */
  pathA?: PathCounters | null;
  pathB?: PathCounters | null;
  kalshiConfig: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number | null;
  } | null;
  polymarketConfig: {
    side: 'yes' | 'no';
    // Both outcome tokens; `tokenId` is the backend virtual derived from
    // `side` (side === 'yes' ? yesTokenId : noTokenId).
    yesTokenId: string;
    noTokenId: string;
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number | null;
  } | null;
  kalshiEnabled: boolean;
  polymarketEnabled: boolean;
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  /**
   * Sizing strategy for the resting maker order. 'fixed' (default) rests at
   * a fixed size equal to the rule's maxOrderSize (avoids amend churn on
   * high-volume markets); 'dynamic' sizes the maker to the counter
   * platform's top-of-book.
   */
  makerSizeMode?: 'dynamic' | 'fixed';
  /**
   * Per-rule counter-hedge pricing mode. `null` (or absent) means "inherit
   * the global `counterHedgeModeDefault`". Otherwise overrides it for this
   * rule. Updated via the existing rule PATCH.
   */
  counterHedgeMode?: 'always-threshold' | 'gtc-to-sum-one' | 'market-to-hedge' | null;
  /**
   * Counter-stuck market-hedge timeout (seconds). If the resting counter
   * maker hasn't filled after N seconds the engine cancels it and hedges
   * at market. `null` (or absent) = off — the opener then inherits the
   * group's `counterStuckMarketHedgeSeconds` fallback (both empty = off).
   */
  counterStuckMarketHedgeSeconds?: number | null;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  status: EntryRuleStatus;
  contractsBoughtKalshi: number;
  contractsBoughtPolymarket: number;
  totalVolumeExecutedContracts: number;
  activeKalshiOrderId: string | null;
  activePolymarketOrderId: string | null;
  lastExecutionAt: string | null;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // 4 per-(platform, side) maker toggles. See backend EntryRule schema
  // for the path-exclusivity + position-bought-lock contract.
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
  // Entry strategy: 'maker-first' (default) rests a maker on one leg and
  // hedges via FAK taker; 'taker-taker' fires both legs as parallel takers
  // when the combined gross cost (both fees in) clears the threshold and
  // falls back to a balancing maker on any asymmetry.
  entryStrategy?: EntryStrategy;
  // Taker-taker balancing state, engine-managed. Frontend reads them to
  // render the "BAL ←" indicator on the rules table.
  balancingLeg?: 'kalshi' | 'polymarket' | null;
  balancingLegVwap?: number | null;
  balancingContractsRemaining?: number;
  // Out-of-session hedge-check safety net (see hedge-watchdog).
  hedgeCheckEnabled?: boolean;
  hedgeCheckIntervalMs?: number;
  maxUnhedgedContracts?: number | null;
  hedgeCheckConfirmations?: number;
}

export interface EntryExecution {
  _id: string;
  ruleId: string;
  pairId: string;
  pairName: string;
  direction: 'kalshi_maker' | 'polymarket_maker';
  makerLeg: {
    orderId: string;
    platform: string;
    side: string;
    price: number;
    contracts: number;
    fee: number;
    status: string;
  };
  takerLeg: {
    orderId: string;
    platform: string;
    side: string;
    price: number;
    contracts: number;
    fee: number;
    orderType: string;
    status: string;
  };
  totalVolumeUsd: number;
  effectiveTnaPercent: number;
  capitalDeployed: number;
  overallStatus:
    | 'complete'
    | 'partial'
    | 'maker_only'
    | 'failed'
    | 'pending_counter';
  errorMessage?: string;
  createdAt: string;
}

export interface CreateEntryRuleDto {
  pairId: string;
  pairName: string;
  expiryDate: string;
  customExpiryDate?: string | null;
  ruleType?: EntryRuleType;
  thresholdSum?: number;
  pathAThresholdSum?: number;
  pathBThresholdSum?: number;
  kalshiConfig?: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  polymarketConfig?: {
    side: 'yes' | 'no';
    // Both outcome tokens are sent up front; the backend derives the
    // active token from `side`, so a path flip never needs a token edit.
    yesTokenId: string;
    noTokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  kalshiEnabled?: boolean;
  polymarketEnabled?: boolean;
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
  entryStrategy?: EntryStrategy;
  makerSizeMode?: 'dynamic' | 'fixed';
  counterHedgeMode?:
    | 'always-threshold'
    | 'gtc-to-sum-one'
    | 'market-to-hedge'
    | null;
  counterStuckMarketHedgeSeconds?: number | null;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  hedgeCheckEnabled?: boolean;
  hedgeCheckIntervalMs?: number;
  maxUnhedgedContracts?: number;
  hedgeCheckConfirmations?: number;
}

export interface UpdateEntryRuleDto {
  thresholdSum?: number;
  pathAThresholdSum?: number;
  pathBThresholdSum?: number;
  kalshiConfig?: {
    side?: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  polymarketConfig?: {
    side?: 'yes' | 'no';
    yesTokenId: string;
    noTokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  maxVolumeContracts?: number;
  volumePeriod?: 'hour' | 'day';
  customExpiryDate?: string | null;
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
  entryStrategy?: EntryStrategy;
  makerSizeMode?: 'dynamic' | 'fixed';
  counterHedgeMode?: 'always-threshold' | 'gtc-to-sum-one' | 'market-to-hedge' | null;
  counterStuckMarketHedgeSeconds?: number | null;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  hedgeCheckEnabled?: boolean;
  hedgeCheckIntervalMs?: number;
  maxUnhedgedContracts?: number;
  hedgeCheckConfirmations?: number;
}

/**
 * A pair surfaced by the pair-manager as `manual-confirmed` and `active`
 * — these are the only pairs eligible for entry rule creation. Schema is
 * kept loose because the pair-manager's `manual-confirmed` endpoint is
 * the canonical source: extra fields just pass through.
 */
export interface ManualConfirmedPair {
  _id: string;
  pairName: string;
  polymarketTitle?: string;
  kalshiTitle?: string;
  expiryDate?: string | null;
  category?: string | null;
  subcategory?: string | null;
  openerGroupId?: string | null;
  /**
   * Persisted per-pair opener kill switch (`prediction_pairs.openerEnabled`).
   * Read by the maker-flag arm path to decide whether to auto-enable the pair
   * before arming a leg — the backend rejects arming a leg on an
   * `openerEnabled: false` pair (`assertPairOpenerEnabled` → 400). Default
   * false on newly manual-confirmed pairs.
   */
  openerEnabled?: boolean;
  status?: string;
  archived?: boolean;
  // Polymarket leg identifiers (mirrored to entry rule polymarketConfig)
  polymarket?: {
    yesTokenId?: string;
    noTokenId?: string;
    conditionId?: string;
    side?: 'yes' | 'no';
    marketUrl?: string;
  } | null;
  // Kalshi leg identifiers (mirrored to entry rule kalshiConfig)
  kalshi?: {
    ticker?: string;
    side?: 'yes' | 'no';
    marketUrl?: string;
  } | null;
}

export interface NormalizedOrder {
  id: string;
  platform: 'kalshi' | 'polymarket';
  marketId: string;
  marketTitle?: string;
  marketUrl?: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  price: number;
  quantity: number;
  filledQuantity: number;
  remainingQuantity: number;
  status: string;
  isEntry: boolean;
  ruleId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrdersSummary {
  kalshi: { count: number; totalVolume: number };
  polymarket: { count: number; totalVolume: number };
}

export interface EntryOrdersResponse {
  orders: NormalizedOrder[];
  summary: OrdersSummary;
}
